import React from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  FormValidationResult,
  HasValidationFn,
  RegisterValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import { OutlineButton } from '@noths/polaris-client-ribbons-design-system';
import type { Dictionary } from '@noths/polaris-dev-ts-recipes';

import { Checkbox } from 'src/components/atoms/Checkbox/Checkbox';
import { Dropdown } from 'src/components/atoms/Dropdown/Dropdown';
import { ErrorMessage } from 'src/components/atoms/ErrorMessage/ErrorMessage';
import { TextInput } from 'src/components/atoms/TextInput/TextInput';
import { WarningMessage } from 'src/components/atoms/WarningMessage/WarningMessage';
import { RadioButtonGroup } from 'src/components/molecules/RadioButtonGroup/RadioButtonGroup';
import {
  BUSINESS_REGISTRATION_TYPES,
  SOURCES,
} from 'src/components/organisms/Signup/constants/aboutYouOptions';
import {
  COMPANY_NAME_COPY,
  HOW_DID_YOU_HEAR_ABOUT_US_COPY,
  ID_CHECK_COPY,
  PHONE_NUMBER_COPY,
} from 'src/components/organisms/Signup/constants/copy';
import { PARTNER_SOURCE_TESTID } from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import {
  ABOUT_YOU_VALIDATION_CONTACT_FIELDS,
  ABOUT_YOU_VALIDATION_RULE_NAMES,
} from 'src/components/organisms/Signup/validation';
import * as styles from 'src/styles/styles';
import type { RegistrationForm, RegistrationType } from 'src/types/registration';
import { CompanyTradingAddress } from './CompanyTradingAddress';
import { PhoneNumber } from './PhoneNumber';

export const MIN_COMPANY_NAME_LENGTH = 4;

export interface AboutYouProps {
  clearError: (errorName: string) => void;
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValidator: (subsectionValidationRuleNames?: string[]) => FormValidationResult<Dictionary>;
  formValues: RegistrationForm;
  hasValidation: HasValidationFn;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  pageForward: () => boolean;
  registerValidation: RegisterValidationFn<RegistrationForm>;
  setFormValue: SetFormValueFn<RegistrationForm>;
}

export const AboutYou = ({
  clearError,
  deregisterValidation,
  errors,
  formValidator,
  formValues,
  hasValidation,
  onBlur,
  onFormChange,
  pageForward,
  registerValidation,
  setFormValue,
}: AboutYouProps) => {
  const formContainsErrors = Boolean(Object.keys(errors).length);

  const handleClickNext = () => {
    const { isValid } = formValidator(ABOUT_YOU_VALIDATION_RULE_NAMES);
    if (isValid) {
      pageForward();
    }
  };

  return (
    <section css={styles.pageSection}>
      <WarningMessage />
      <h2 css={styles.sectionHeading}>Section 1</h2>
      <h3 css={styles.sectionTitle}>Tell us about you</h3>
      <div css={styles.subSection}>
        <TextInput
          css={styles.textField}
          data-position="1"
          data-section="1"
          error={errors.companyName as string | undefined}
          id="partner-company-name"
          label={COMPANY_NAME_COPY}
          minLength={MIN_COMPANY_NAME_LENGTH}
          name="companyName"
          onBlur={onBlur}
          onChange={onFormChange}
          required
          value={formValues.companyName}
        />
      </div>
      <div css={styles.subSection}>
        <RadioButtonGroup
          accessibleLabel="Is your business registered?"
          additionalCss={styles.grouped}
          data-position="2"
          data-section="1"
          error={errors['registrationType'] as string | undefined}
          name="registrationType"
          onBlur={onBlur}
          onChange={(registrationType) =>
            setFormValue('registrationType', registrationType as RegistrationType)
          }
          radioButtonData={BUSINESS_REGISTRATION_TYPES}
          required
          value={formValues.registrationType}
        />
      </div>
      <div css={styles.subSection}>
        <CompanyTradingAddress
          clearError={clearError}
          errors={errors}
          formValues={formValues}
          hasValidation={hasValidation}
          onBlur={onBlur}
          onFormChange={onFormChange}
          setFormValue={setFormValue}
          validatableFields={ABOUT_YOU_VALIDATION_CONTACT_FIELDS}
        />
      </div>
      <p css={styles.label}>{PHONE_NUMBER_COPY}</p>
      <div css={[styles.subSection, styles.phoneNumberSection]}>
        <PhoneNumber
          deregisterValidation={deregisterValidation}
          errors={errors}
          formValues={formValues}
          onBlur={onBlur}
          onFormChange={onFormChange}
          registerValidation={registerValidation}
        />
      </div>
      <div css={styles.subSection}>
        <Dropdown
          css={styles.dropdown}
          data-position="5"
          data-section="1"
          data-testid={PARTNER_SOURCE_TESTID}
          error={errors.partnerSource as string | undefined}
          id="partner-source"
          label={HOW_DID_YOU_HEAR_ABOUT_US_COPY}
          name="partnerSource"
          onBlur={onBlur}
          onChange={onFormChange}
          options={SOURCES}
          required
          value={formValues.partnerSource}
        />
      </div>
      <Checkbox
        checked={formValues.partnerInvited}
        css={styles.singleCheckboxWithLabel}
        data-position="6"
        data-section="1"
        error={errors['partnerInvited'] as string | undefined}
        key="partnerInvited"
        label="Please select this box if you have been invited by a member of Notonthehighstreet to sell with us"
        name="partnerInvited"
        onChange={onFormChange}
        required
      />
      <Checkbox
        checked={formValues.partnerAgreesToProvideId}
        css={styles.singleCheckboxWithLabel}
        data-position="7"
        data-section="1"
        error={errors.partnerAgreesToProvideId as string | undefined}
        key="partnerAgreesToProvideId"
        label={ID_CHECK_COPY}
        name="partnerAgreesToProvideId"
        onBlur={onBlur}
        onChange={onFormChange}
        required
      />
      {Object.keys(errors).length > 0 && <ErrorMessage />}
      <div css={styles.navigationButtonsGrid}>
        <OutlineButton
          disabled={formContainsErrors}
          id="next-from-page-about-you"
          onClick={handleClickNext}
        >
          Next
        </OutlineButton>
      </div>
    </section>
  );
};
